import React, { useCallback, useEffect, useState } from 'react';
import CheckoutLayout from '../../CheckoutLayout';
import { Input, Select, Text } from '~/components';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCheckout, useSalesFunnel } from '~/stores';
import useAppNavigation from '~/hooks/useAppNavigation';
import { Keyboard } from 'react-native';
import { getVehicleColorsUseCase } from '~/useCases/AssegurouAutoUseCases/GetVehicleColors';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import { CheckoutStepNames } from '~/utils/salesFunnel';

interface VehicleInfoSchema {
  renavam: number;
  chassis: string;
  color: string;
}

const schema = yup
  .object()
  .shape({
    renavam: yup
      .string()
      .required('Este campo é obrigatório')
      .length(11, 'Este campo deve possuir 11 caracteres'),
    chassis: yup
      .string()
      .required('Este campo é obrigatório')
      .length(17, 'Este campo deve possuir 17 caracteres'),
    color: yup.string().required('Insira a cor do veículo para continuar'),
  })
  .required();

const VehicleInfo: React.FC = () => {
  const { setPage, setInsuranceFormData } = useCheckout();
  const { form } = useMarisaAutoForm();
  const { push } = useAppNavigation();
  const { newStepLog } = useSalesFunnel();

  const { params } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_Checkout'>>();

  const [vehicleColorsOptions, setVehicleColorsOptions] = useState<
    Select.Option[]
  >([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VehicleInfoSchema>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    async (data: VehicleInfoSchema) => {
      Keyboard.dismiss();

      setInsuranceFormData({
        vehicleInfo: {
          color: data.color,
          renavam: data.renavam,
          chassi: data.chassis,
          plate: form?.licensePlate,
        },
      });

      const nextStep = (params.step || 0) + 1;
      setPage(nextStep);

      push('MarisaServicos_Checkout', { step: nextStep });
    },
    [form?.licensePlate, params.step, push, setInsuranceFormData, setPage]
  );

  useEffect(() => {
    async function loadColors() {
      const optionsResponse = await getVehicleColorsUseCase();

      setVehicleColorsOptions(optionsResponse);
    }

    loadColors();
  }, []);

  useEffect(() => {
    newStepLog(CheckoutStepNames.VEHICLE_INFO);
  }, [newStepLog]);

  return (
    <CheckoutLayout isValid onSubmit={handleSubmit(onSubmit)}>
      <Text className="text-xl text-gray-600 mb-4">
        Agora precisamos de algumas informações sobre o veículo
      </Text>

      <Text className="text-sm text-gray-400 mb-8">
        Insira as informações abaixo. Elas pode ser encontradas no documento do
        veículo.
      </Text>

      <Input
        control={control}
        name="renavam"
        label="RENAVAM"
        placeholder="Insira o número do RENAVAM"
        error={errors.renavam?.message}
        maxLength={11}
        keyboardType="number-pad"
        maskConfig={{ type: 'only-numbers' }}
      />

      <Input
        control={control}
        name="chassis"
        label="Chassi"
        placeholder="Insira o número do chassi"
        error={errors.chassis?.message}
        maxLength={17}
        keyboardType="ascii-capable"
      />

      <Select
        control={control}
        name="color"
        label="Cor do veículo"
        placeholder="Insira a cor do veículo"
        error={errors.color?.message}
        items={vehicleColorsOptions}
      />
    </CheckoutLayout>
  );
};

export default VehicleInfo;
